import { Box, Flex } from '@chakra-ui/react';
import {
  Children,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { CarouselChevron } from './CarouselChevron';
import { CarouselContext } from './carouselContext';

type Props = {
  'aria-label': string;
  // CarouselItems.
  children: ReactNode;
  // The narrowest an item gets before the row scrolls instead.
  minItemWidth: number;
  // The fewest items in view, however narrow the row.
  minVisible?: number;
  gap?: number;
  nextLabel?: string;
  prevLabel?: string;
  // Kept in view, so one picked elsewhere (from the URL, say) isn't hidden
  // past the edge.
  selectedIndex?: number;
  testId?: string;
};

const FADE_PX = 56;
// Room around the items inside the scroller for a hovered item's lift and
// shadow, which the scroller would otherwise clip. Negative margins of the
// same size keep the items where they were. The shadow falls mostly below.
const SHADOW_X_PX = 16;
const SHADOW_TOP_PX = 8;
const SHADOW_BOTTOM_PX = 24;

// Fades the items out toward whichever edges have more items past them,
// measured from the items' edges rather than the shadow room beyond them.
const fadeMask = (left: boolean, right: boolean) => {
  if (!left && !right) return undefined;
  const start = left
    ? `transparent ${SHADOW_X_PX}px, black ${SHADOW_X_PX + FADE_PX}px`
    : 'black 0';
  const end = right
    ? `black calc(100% - ${SHADOW_X_PX + FADE_PX}px), transparent calc(100% - ${SHADOW_X_PX}px)`
    : 'black 100%';
  return `linear-gradient(to right, ${start}, ${end})`;
};

/**
 * A row of items that scrolls a whole item at a time: every item in one row
 * when it has room for them at `minItemWidth`, else as many as fit, with
 * chevrons and a fade on the sides that have more.
 */
export const Carousel = ({
  'aria-label': ariaLabel,
  children,
  minItemWidth,
  minVisible = 2,
  gap = 8,
  nextLabel = 'Next',
  prevLabel = 'Previous',
  selectedIndex,
  testId = 'carousel',
}: Props) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState({ left: false, right: false });
  const [rowWidth, setRowWidth] = useState(0);
  const count = Children.count(children);

  // All the items in one row when it has room for them, else as many as fit.
  const visible = rowWidth
    ? Math.min(
        count,
        Math.max(
          minVisible,
          Math.floor((rowWidth + gap) / (minItemWidth + gap))
        )
      )
    : count;
  // The row's width, less the gaps between the items in view, shared between
  // them.
  const itemBasis = `calc((100% - ${(visible - 1) * gap}px) / ${visible})`;

  const measure = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const { scrollLeft, scrollWidth, clientWidth } = scroller;
    setRowWidth(clientWidth - 2 * SHADOW_X_PX);
    // A pixel of slack: snapped and zoomed positions land on fractions.
    setOverflow({
      left: scrollLeft > 1,
      right: scrollLeft + clientWidth < scrollWidth - 1,
    });
  }, []);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(scroller);
    return () => observer.disconnect();
  }, [measure]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller || selectedIndex == null) return;
    const item = scroller.children[selectedIndex] as HTMLElement | undefined;
    if (!item) return;
    const { offsetLeft, offsetWidth } = item;
    const left = offsetLeft - SHADOW_X_PX;
    if (
      left < scroller.scrollLeft ||
      left + offsetWidth + 2 * SHADOW_X_PX >
        scroller.scrollLeft + scroller.clientWidth
    ) {
      scroller.scrollTo({ left, behavior: 'smooth' });
    }
  }, [selectedIndex]);

  // One item per click, onto the next item's edge.
  const page = useCallback((direction: 1 | -1) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const items = Array.from(scroller.children) as HTMLElement[];
    const { scrollLeft, scrollWidth } = scroller;
    // Where the scroller sits with an item at its leading edge.
    const leftOf = (item: HTMLElement) => item.offsetLeft - SHADOW_X_PX;
    const target =
      direction > 0
        ? items.find((item) => leftOf(item) > scrollLeft + 1)
        : items.findLast((item) => leftOf(item) < scrollLeft - 1);
    scroller.scrollTo({
      left: target ? leftOf(target) : direction > 0 ? scrollWidth : 0,
      behavior: 'smooth',
    });
  }, []);

  // A horizontal scroll gesture advances one item in its direction. Left to
  // the browser, mandatory snapping pulls a short trackpad swipe back to the
  // item it started on. A trackpad keeps firing wheel events through its
  // momentum, so the gesture only counts once, until its events stop.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    let gestureTimer: ReturnType<typeof setTimeout> | undefined;

    const handleWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      event.preventDefault();
      if (!gestureTimer) page(event.deltaX > 0 ? 1 : -1);
      clearTimeout(gestureTimer);
      gestureTimer = setTimeout(() => {
        gestureTimer = undefined;
      }, 200);
    };

    // Not passive, so it can take over from the browser's own scroll.
    scroller.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      scroller.removeEventListener('wheel', handleWheel);
      clearTimeout(gestureTimer);
    };
  }, [page]);

  return (
    <CarouselContext.Provider value={{ itemBasis }}>
      <Box pos="relative">
        {overflow.left && (
          <CarouselChevron
            direction="left"
            label={prevLabel}
            testId={testId}
            onClick={() => page(-1)}
          />
        )}
        <Flex
          aria-label={ariaLabel}
          gap={`${gap}px`}
          mb={`-${SHADOW_BOTTOM_PX - SHADOW_TOP_PX}px`}
          mx={`-${SHADOW_X_PX}px`}
          overflowX="auto"
          pb={`${SHADOW_BOTTOM_PX}px`}
          // The items' offsets are measured from here.
          pos="relative"
          pt={`${SHADOW_TOP_PX}px`}
          px={`${SHADOW_X_PX}px`}
          ref={scrollerRef}
          role="group"
          sx={{
            maskImage: fadeMask(overflow.left, overflow.right),
            scrollPaddingInline: `${SHADOW_X_PX}px`,
            scrollSnapType: 'x mandatory',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
          onScroll={measure}
        >
          {children}
        </Flex>
        {overflow.right && (
          <CarouselChevron
            direction="right"
            label={nextLabel}
            testId={testId}
            onClick={() => page(1)}
          />
        )}
      </Box>
    </CarouselContext.Provider>
  );
};
